import { useMemo } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { ReportingScope } from '@/domain/reportingCompare';
import { perStudioSeries, singleStudioScope } from '@/domain/reportingCompare';
import { Money } from '@/components/reporting/Money';
import SeededBadge from '@/components/reporting/SeededBadge';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import ReportHeatmap, { type HeatmapColumn, type HeatmapRowData } from '@/components/reporting/ReportHeatmap';
import {
  unworkedLeads,
  newLeadsBySourceByDay,
  campaignPerformance,
  agentPerformance,
  inquiryArrivalHeatmap,
  artistLinkDeposits,
  conversionStatusBlock,
  socialConversionStrikeRate,
} from '@/domain/reportingEngine';

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
}

function hourLabel(hour: number): string {
  const period = hour < 12 ? 'a' : 'p';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}${period}`;
}

function pct(n: number | null): string {
  return n === null ? '—' : `${Math.round(n * 100)}%`;
}

function ageDays(oldestIso: string): number {
  return Math.floor((Date.now() - new Date(oldestIso).getTime()) / (24 * 60 * 60_000));
}

/**
 * Leads & agents tab (leak-first reporting, Task 9): where leads come from,
 * what they cost, and who converts them. New leads by source (stacked),
 * cost per booked deposit per campaign, per-agent call/deposit performance,
 * and the inquiry-arrival heatmap (the staffing signal).
 */
export default function LeadsAgentsTab({ scope }: { scope: ReportingScope }) {
  const customers = useBrainStore((s) => s.customers);
  const marketingCampaigns = useBrainStore((s) => s.marketingCampaigns);
  const deposits = useBrainStore((s) => s.deposits);
  const agents = useBrainStore((s) => s.agents);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const { days, studioIds, compareMode, moneyVisible } = scope;

  const single = singleStudioScope(studioIds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const unworked = useMemo(() => unworkedLeads(single), [single, customers]);

  const sourceTrend = useMemo(() => {
    const rows = newLeadsBySourceByDay(days, single);
    return rows.map((r) => ({ day: dayLabel(r.dayIso), Web: r.web, Social: r.social, Campaign: r.campaign }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, single, customers]);

  // Campaigns are attributed by the paying customer's campaign, not by studio,
  // so this stays all-studios (matches the old LeadsTab).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const campaigns = useMemo(() => campaignPerformance(), [marketingCampaigns, deposits, customers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const perAgent = useMemo(() => agentPerformance(days, single), [days, single, agents, customers, deposits]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const arrival = useMemo(() => inquiryArrivalHeatmap(single), [single, customers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusBlock = useMemo(() => conversionStatusBlock(single), [single, customers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const social = useMemo(() => socialConversionStrikeRate(days, single), [days, single, customers, deposits]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const artistLink = useMemo(() => artistLinkDeposits(days, single), [days, single, deposits, customers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const series = useMemo(
    () => (compareMode ? perStudioSeries(studioIds, (sid) => unworkedLeads(sid).count) : []),
    [compareMode, studioIds, customers],
  );

  // Only render the hours that actually carry inquiries (clamped to a business
  // window) so the heatmap stays compact without silently dropping arrivals.
  const { minHour, maxHour } = useMemo(() => {
    let lo = 24;
    let hi = -1;
    for (const row of arrival) {
      row.hours.forEach((count, hour) => {
        if (count > 0) {
          lo = Math.min(lo, hour);
          hi = Math.max(hi, hour);
        }
      });
    }
    if (hi < 0) return { minHour: 8, maxHour: 20 };
    return { minHour: Math.min(lo, 8), maxHour: Math.max(hi, 20) };
  }, [arrival]);

  const hourColumns: HeatmapColumn[] = [];
  for (let h = minHour; h <= maxHour; h += 1) hourColumns.push({ key: `h${h}`, label: hourLabel(h) });
  const arrivalRows: HeatmapRowData[] = arrival.map((row) => ({
    label: row.label,
    cells: hourColumns.map((_, i) => {
      const hour = minHour + i;
      return { colKey: `h${hour}`, value: row.hours[hour] ?? 0, title: `${row.label} ${hourLabel(hour)}: ${row.hours[hour] ?? 0} inquiries` };
    }),
  }));

  if (compareMode) {
    return (
      <div className="space-y-4 max-w-6xl" data-testid="reporting-leads">
        <section className="panel p-4 overflow-x-auto">
          <h2 className="heading-serif text-lg text-cream mb-4">Leads, studios compared</h2>
          <table className="w-full text-sm" data-testid="leads-compare-table">
            <thead>
              <tr>
                <th className="text-left label-small-caps pb-2">Metric</th>
                {series.map((s) => (
                  <th key={s.studioId} className="text-right label-small-caps pb-2">
                    {studios[s.studioId]?.name ?? s.studioId}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gold-dim/15">
                <td className="py-2 text-cream-soft">Un-worked leads</td>
                {series.map((s) => (
                  <td key={s.studioId} className="py-2 text-right font-mono text-cream">
                    {s.value.toLocaleString('en-AU')}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-leads">
      {/* Un-worked leads + social-DM conversion tiles */}
      <section className="grid grid-cols-3 gap-3">
        <Tile label="Un-worked leads" info="An active lead (new/qualifying) the team has never reached out to (no outbound conversation entry).">
          {unworked.count.toLocaleString('en-AU')}
          {unworked.oldestIso ? (
            <span className="text-cream-faint text-base"> · oldest {ageDays(unworked.oldestIso)}d</span>
          ) : null}
        </Tile>
        <Tile
          label="Social-DM conversion"
          testid="leads-social-conversion"
          info="Of social-DM leads first-touched in this window, the share that paid a non-artist-link deposit. Artist-link deposits are counted separately (below) so they never inflate this rate."
        >
          {social.strikeRatePct === null ? '—' : `${Math.round(social.strikeRatePct)}%`}
          <span className="text-cream-faint text-base">
            {' · '}
            {social.conversions.toLocaleString('en-AU')}/{social.socialLeads.toLocaleString('en-AU')}
            {social.excludedArtistLink > 0 ? ` · ${social.excludedArtistLink} artist-link excl.` : ''}
          </span>
        </Tile>
      </section>

      {/* Lead-status chips */}
      <section className="panel p-4" data-testid="leads-status-chips">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Lead status
          <InfoTip>
            Where every lead in scope sits right now, from lead stage + the sequence red split: Active (green) =
            qualifying/deposit-pending, Unresponsive (light red) = never reached, Closed (dark red) = worked but lost.
          </InfoTip>
        </h2>
        <div className="flex flex-wrap gap-2 mt-3">
          {statusBlock.filter((s) => s.count > 0).map((s) => (
            <span
              key={s.key}
              data-testid={`leads-status-chip-${s.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-gold-dim/25 bg-gold/5 px-3 py-1 text-xs text-cream-soft"
            >
              {s.label}
              <span className="font-mono text-cream">{s.count.toLocaleString('en-AU')}</span>
            </span>
          ))}
        </div>
      </section>

      {/* New leads by day + source */}
      <section className="panel p-4" data-testid="leads-by-source">
        <h2 className="heading-serif text-lg text-cream mb-1">
          New leads by source
          <InfoTip>
            New leads by first-touch day, grouped by where they came from: paid campaign (has a campaign
            attribution), social DM (Meta/Instagram), or web/direct (everything else). Over the selected range.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Web · social DM · paid campaign, per day this week
        </p>
        <ReportBarChart
          data={sourceTrend}
          xKey="day"
          stacked
          series={[
            { key: 'Social', name: 'Social DM', color: REPORT_COLORS.gold },
            { key: 'Web', name: 'Web / direct', color: REPORT_COLORS.azure },
            { key: 'Campaign', name: 'Paid campaign', color: REPORT_COLORS.mint },
          ]}
        />
      </section>

      {/* Cost per booked deposit per campaign */}
      <section className="panel p-4" data-testid="leads-campaigns">
        <h2 className="heading-serif text-lg text-cream mb-1 flex items-center gap-2">
          Cost per booked deposit
          <SeededBadge label="Ad spend — pending live Marketing API" />
          <InfoTip>
            Per campaign: seeded spend ÷ deposits attributed to it (via the paying customer&apos;s campaign). Dash =
            none converted yet.
          </InfoTip>
        </h2>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Campaign</th>
                <th className="text-right px-2 py-2">Spend</th>
                <th className="text-right px-2 py-2">Messages</th>
                <th className="text-right px-2 py-2">Deposits</th>
                <th className="text-right px-2 py-2">Cost / deposit</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 font-serif text-cream-soft text-sm">
                    No campaigns yet.
                  </td>
                </tr>
              ) : (
                campaigns.map((c) => (
                  <tr key={c.campaign.id} className="border-t border-gold-dim/15" data-testid={`leads-campaign-${c.campaign.id}`}>
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{c.campaign.name}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">
                      <Money amountAud={c.spendAud} visible={moneyVisible} />
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{c.messagesSent}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream">{c.deposits}</td>
                    <td className={clsx('px-2 py-2 text-right font-mono', c.costPerDepositAud === null ? 'text-cream-faint' : 'text-gold-bright')}>
                      {c.costPerDepositAud === null ? '—' : <Money amountAud={c.costPerDepositAud} visible={moneyVisible} />}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Artist-link deposits */}
      <section className="panel p-4" data-testid="leads-artist-link-deposits">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Artist-link deposits
          <InfoTip>
            Paid deposits taken through an artist&apos;s own booking link in the window, grouped by artist with the
            client names. Reported separately from — and excluded from — the social-DM conversion rate above.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          {artistLink.total.toLocaleString('en-AU')} deposits ·{' '}
          <Money amountAud={artistLink.amountAud} visible={moneyVisible} /> in scope
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Artist</th>
                <th className="text-right px-2 py-2">Deposits</th>
                <th className="text-right px-2 py-2">Value</th>
                <th className="text-left px-2 py-2">Clients</th>
              </tr>
            </thead>
            <tbody>
              {artistLink.byArtist.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-2 py-3 font-serif text-cream-soft text-sm">
                    No artist-link deposits in scope.
                  </td>
                </tr>
              ) : (
                artistLink.byArtist.map((r) => (
                  <tr key={r.artistId} className="border-t border-gold-dim/15" data-testid={`leads-artist-link-row-${r.artistId}`}>
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">
                      {artists[r.artistId]?.displayName ?? r.artistId}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-gold-bright">{r.count}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">
                      <Money amountAud={r.amountAud} visible={moneyVisible} />
                    </td>
                    <td className="px-2 py-2 font-inter text-cream-soft text-xs">{r.clientNames.join(', ')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-agent performance */}
      <section className="panel p-4" data-testid="leads-agents">
        <h2 className="heading-serif text-lg text-cream mb-3">
          Agents
          <InfoTip>
            Per bookings-agent for the selected span: outbound + inbound calls, deposits credited to them, and
            conversion = deposits ÷ total calls. A dash means no calls have been dialled yet (not zero conversion).
          </InfoTip>
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Agent</th>
                <th className="text-right px-2 py-2">
                  Outbound <SeededBadge label="Call volume — accrues live" />
                </th>
                <th className="text-right px-2 py-2">Inbound</th>
                <th className="text-right px-2 py-2">Deposits</th>
                <th className="text-right px-2 py-2">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {perAgent.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 font-serif text-cream-soft text-sm">
                    No agents in scope.
                  </td>
                </tr>
              ) : (
                perAgent.map((r) => (
                  <tr key={r.agentId} className="border-t border-gold-dim/15" data-testid={`leads-agent-${r.agentId}`}>
                    <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{r.outboundCalls}</td>
                    <td className="px-2 py-2 text-right font-mono text-cream-soft">{r.inboundCalls}</td>
                    <td className="px-2 py-2 text-right font-mono text-gold-bright">{r.deposits}</td>
                    <td className={clsx('px-2 py-2 text-right font-mono', r.conversion === null ? 'text-cream-faint' : 'text-cream')}>
                      {pct(r.conversion)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Inquiry-arrival heatmap */}
      <section className="panel p-4" data-testid="leads-arrival-heatmap">
        <h2 className="heading-serif text-lg text-cream mb-1">
          When inquiries land
          <InfoTip>
            First-touch count by weekday × hour of day, all-time. Darker cells = more inquiries arriving then — a
            staffing signal for when to have someone on the phones.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          First touch by weekday × hour — a staffing signal
        </p>
        <ReportHeatmap
          columns={hourColumns}
          rows={arrivalRows}
          tone="gold"
          caption="Darker = more inquiries arriving in that hour"
          emptyLabel="No inquiries recorded yet."
        />
      </section>
    </div>
  );
}

function Tile({ label, info, testid, children }: { label: string; info?: string; testid?: string; children: ReactNode }) {
  return (
    <div className="panel-dark p-4" data-testid={testid}>
      <p className="label-small-caps flex items-center gap-1">
        {label}
        {info ? <InfoTip>{info}</InfoTip> : null}
      </p>
      <p className="font-cinzel text-3xl text-cream mt-2">{children}</p>
    </div>
  );
}
